import { reactive } from 'vue'
import regexPatterns from '~/assets/js/constants/regex-patterns'

export default class Part {
  constructor(data) {
    this._rawData = data

    this.id = data.id
    this.skuPrefix = data.skuPrefix
    this.name = data.name
    this.icon = data.icon
    this.imageLayer = data.imageLayer
    this.followerOf = data.followerOf?.skuPrefix
    this.excludeFromProductDetails = data.excludeFromProductDetails
    this.disableInfiniteSwatchIcon = data.disableInfiniteSwatchIcon
    this.excludeFromAdFeeds = data.excludeFromAdFeeds
    this.material = data.material
    this.additionalSpec = data.additionalSpec
    this.specificationType = data.specificationType
    // Trick Vue into thinking this instance already has an observer to prevent it adding a new one
    this.__ob__ = reactive({}).__ob__
  }

  addOptions(getOption) {
    this.options = this._rawData.options.map((option) => {
      return getOption(option.id)
    })

    return this.options
  }

  get isLensPart() {
    return this.options[0].partType === 'lenses'
  }

  get minimumPrice() {
    const prices = this.options.map((option) => option.price).filter((price) => price)

    if (!prices.length) return 0

    return Math.min(...prices)
  }

  get minimumPrescriptionPrice() {
    const prices = this.options
      .filter(
        (option) =>
          regexPatterns.prescriptionSunglasses.test(option.sku) &&
          !regexPatterns.prescriptionEyeglasses.test(option.sku)
      )
      .map((option) => option.price)
      .filter((price) => price)

    if (!prices.length) return 0

    return Math.min(...prices)
  }

  get minimumEyeglassesPrice() {
    const prices = this.options
      .filter((option) => regexPatterns.prescriptionEyeglasses.test(option.sku))
      .map((option) => option.price)
      .filter((price) => price)

    if (!prices.length) return 0

    return Math.min(...prices)
  }

  get minimumCompareAtPrice() {
    const prices = this.options.map((option) => option.compareAtPrice).filter((price) => price)

    if (!prices.length) return 0

    return Math.min(...prices)
  }

  get stockManagedSKUPrefix() {
    if (!this._stockManagedSKUPrefix) this._stockManagedSKUPrefix = this.options[0].sku.split('_')[0]
    return this._stockManagedSKUPrefix
  }

  get type() {
    return this.options[0]?.partType
  }

  getFilteredOptions(customisedProduct) {
    const pageMode = customisedProduct.product.pageMode

    return [...this.options]
      .filter((option) => {
        // Filter out parts that can't be sold on their own (part.allowAlone can be unset, hence type checking false)
        if (pageMode === 'parts' && option.allowAlone === false) return false

        // Filter out lenses that aren't in stock for any lens tech
        const isLensPart = option.partType === 'lenses'
        if (!isLensPart) return true
        const lensChoice = customisedProduct.lensChoice

        let lensColourInStockForAnyLensTech = this.options.some((option) => option.quantity > 0)

        // If the current option is a water lens then return either the polarised or non-polarised option depending
        // on the type of 8KO selection.
        if (!lensColourInStockForAnyLensTech && option.isWaterLens) {
          if (
            (lensChoice?.lensTech === '8ko' || lensChoice?.lensTech === '4ko') &&
            option.lensTech === '8kowater'
          ) {
            lensColourInStockForAnyLensTech = true
          } else if (
            (lensChoice?.lensTech === '8kop' || lensChoice?.lensTech === '4kop') &&
            option.lensTech === '8kopwater'
          ) {
            lensColourInStockForAnyLensTech = true
          }
        } else if (!lensColourInStockForAnyLensTech && lensChoice?.lensTech.includes('water')) {
          if (lensChoice?.lensTech === '8kowater' && option.lensTech === '8ko') {
            lensColourInStockForAnyLensTech = true
          } else if (lensChoice?.lensTech === '8kopwater' && option.lensTech === '8kop') {
            lensColourInStockForAnyLensTech = true
          }
        }

        // Some lenses have a lens tech defined by a product option, check if the current lens tech is available as an option
        const hasCurrentLensTechAsOption = option.productOptions?.some(
          (productOption) => productOption.lensTech === lensChoice?.lensTech
        )

        if (
          customisedProduct.allowLensTechFiltering &&
          isLensPart &&
          !lensColourInStockForAnyLensTech &&
          !hasCurrentLensTechAsOption
        ) {
          return false
        }

        return true
      })
      .sort((a, b) => {
        // sort so in stock options come first and so the reducer that follows
        // can show those options in stock first when looping through
        // for existing options
        return (b.quantity > 0) - (a.quantity > 0)
      })
      .reduce((accumulatedOptions, currentOption) => {
        if (
          customisedProduct?.lensChoice?.isPrescription &&
          currentOption.isPrescription &&
          !accumulatedOptions.find(
            (accOption) => accOption.skuWithoutLensTech === currentOption.skuWithoutLensTech
          )
        ) {
          accumulatedOptions.push(currentOption)
        } else if (
          !accumulatedOptions.find(
            (accOption) => accOption.skuWithoutLensTech === currentOption.skuWithoutLensTech
          ) &&
          !customisedProduct?.lensChoice?.isPrescription &&
          !currentOption.isPrescription &&
          !customisedProduct?.lensChoice?.isPrescription
        ) {
          accumulatedOptions.push(currentOption)
        } else if (
          !accumulatedOptions.find(
            (accOption) => accOption.skuWithoutLensTech === currentOption.skuWithoutLensTech
          ) &&
          currentOption.partType !== 'lenses'
        ) {
          accumulatedOptions.push(currentOption)
        }

        return accumulatedOptions
      }, [])
  }

  getNonInfiniteAlternative(option) {
    if (option.disableInfiniteRollover) return undefined
    return this.options.find(
      (alt) =>
        (alt.sku === option.sku.replace('_I', '_F') || alt.sku === option.sku.replace('_I', '_M')) &&
        alt.sku !== option.sku
    )
  }

  getInfiniteAlternative(option) {
    if (option.disableInfiniteRollover) return undefined
    return this.options.find(
      (alt) =>
        (alt.sku === option.sku.replace('_F', '_I') || alt.sku === option.sku.replace('_M', '_I')) &&
        alt.sku !== option.sku
    )
  }

  toJSON() {
    return this._rawData
  }

  static fromJSON(data, getOption) {
    const part = new Part(data)
    part.addOptions(getOption)
    return part
  }
}
